import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import { registerSchema } from '../../generic-codec.ts';
import type { AllRawData, RawRow } from '../../raw-types.ts';
import type { BtEffect } from '../../../pet-breakthrough.ts';

const PetGradeSchema = defineSchema({
  name: 'petGrade',
  fields: [
    ['grade', 'number'],
    ['returnSapphireAmount', 'number'],
  ],
});
registerSchema(PetGradeSchema);

const PetUpgradeSchema = defineSchema({
  name: 'petUpgrade',
  fields: [
    ['level', 'number'],
    ['requiredExp', 'number'],
    /** HP bonus from `pet_upgrade.character_buff_stats.HP`. Currently 0 in dev
     *  (HP is not in any character_buff_stats jsonb yet) — wired for forward
     *  compat so the schema slot is reserved before any author adds HP buffs. */
    ['hpBonus', 'number?'],
    /**
     * `PetUpgradeData.breakthroughGates`(pet-bt-gate-synergy-fix Task 1/8) — 키별
     * 해금 요구 돌파 단계(`{stats?,skills?,characterBuffStats?,characterBuffSkills?}`,
     * 값=요구 단계). 4개 하위 record 를 한번에 나르는 nested object 라 item.ts/stage.ts
     * 선례(record kind)를 못 쓰고 opaque `{kind:'json'}`(game-config.ts 의 설정 블록들과
     * 동일 패턴)을 쓴다 — 어차피 `PetManager`/`stat-reconstruction`/`applyBreakthroughGate`
     * 소비처가 구조 그대로 통으로 읽지, 키를 순회하며 codec 레벨에서 값을 변형하지 않는다.
     * trailing 슬롯이라 결여(구 dev DB — `dev.pet_upgrade`에 아직 컬럼 없음, Step 6 후속)
     * 시 encode 에서 strip, decode 에서 undefined — 게이트 없음(항상 활성)으로 fail-closed.
     */
    ['breakthroughGates', { kind: 'json' }],
  ],
});
registerSchema(PetUpgradeSchema);

/**
 * v6 돌파 단계 1행 — `PetBreakthroughData`(packages/shared/src/master-data/
 * types/pet.types.ts) 와 필드 동일. 가변 키 record(requiredStones)는 item.ts
 * `stats`/stage.ts `starThresholds` 선례를 따라 `{kind:'record', value:'number'}`
 * — 키 보존, opaque JSON 아님.
 *
 * 레거시 4필드(effectStats/effectSkills/synergyBonus/seedItemGrade)는
 * pet-bt-effects-migration-cleanup Task 3 에서 제거됐다 — `effects`(BtEffect[])
 * 로 완전히 대체됐고 어떤 소비처도 더 이상 4필드를 읽지 않는다(Task 1/2 완료).
 * **재시딩 필요**: 이미 시딩된 verse 의 `serverPets` 컬렉션은 구 9슬롯 포지션으로
 * 인코딩돼 있으므로, 이 스키마 배포 후 반드시 마스터데이터를 재시딩해야 한다 —
 * 그렇지 않으면 구 데이터의 `description`/`effects` 슬롯이 삭제된 레거시 슬롯
 * 자리로 밀려 읽힌다(포지셔널 튜플 = 와이어 계약).
 */
const PetBreakthroughSchema = defineSchema({
  name: 'petBreakthrough',
  fields: [
    ['breakthrough', 'number'],
    ['requiredDupes', 'number'],
    ['requiredStones', { kind: 'record', value: 'number' }],
    ['description', 'string?'],
    /**
     * 돌파 효과 표현(pet-bt-effect-decoupling Task 3 도입) — `BtEffect[]`(효과
     * kind 를 돌파 레벨에서 분리). 반드시 **트레일링**(마지막 슬롯) — 튜플 슬롯
     * 순서는 와이어 계약이라 기존 슬롯 사이에 끼우면 이미 시딩된 데이터의
     * 포지셔널 디코드가 전부 밀려 깨진다. `petUpgrade.breakthroughGates`
     * 선례와 동일하게 opaque `{kind:'json'}` — 소비처가 배열을 통으로 읽지
     * 요소별로 codec 변형을 하지 않는다.
     */
    ['effects', { kind: 'json' }],
  ],
});
registerSchema(PetBreakthroughSchema);

export const PetSchema = defineSchema({
  name: 'serverPets',
  fields: [
    ['id', 'number'],
    ['maxLevel', 'number'],
    ['grade', { kind: 'array', of: 'petGrade' }],
    ['upgrades', { kind: 'array', of: 'petUpgrade' }],
    ['metaTagIds', { kind: 'array', of: 'number' }],
    /** v6 돌파 단계별 요구·효과 테이블. grade5+ 만 채워짐 — 돌파 대상 판정 =
     *  이 배열 존재 여부(파일럿 게이트). decode 후 반드시 `normalizePetBreakthroughs`
     *  를 적용할 것(아래 함수 doc 참조 — 포지셔널 gap 이 null 오염을 만든다). */
    ['breakthroughs', { kind: 'array', of: 'petBreakthrough' }],
  ],
  lookupKeys: ['id'],
});

/** `null` → `undefined`(키 삭제) 정규화 대상 — v6 optional 필드. `requiredStones`
 *  는 §4.2 전제상 모든 단계에 항상 채워지므로 제외한다(만약 null 이면 gap 이 아니라
 *  진짜 authoring 누락이라 그대로 노출해야 감지된다). */
const PET_BREAKTHROUGH_NULLABLE_OPTIONAL_KEYS = ['description', 'effects'] as const;

/**
 * `decode(PetSchema, tuple).breakthroughs` 전용 post-decode 정규화.
 *
 * **왜 필요한가**: 코덱은 포지셔널 튜플이다 — 한 돌파 단계 행에서 순서상 앞선
 * optional 필드가 비어 있는데 그보다 뒤 필드가 채워져 있으면, 앞 필드는 encode
 * 시 `null` 로 채워지고(trailing 이 아니므로 strip 되지 않음) decode 시에도
 * `null` 그대로 복원된다. 레거시 4필드 제거(pet-bt-effects-migration-cleanup
 * Task 3) 이후 `effects` 앞의 optional 슬롯은 `description` 하나뿐이라, gap 은
 * 다음 단일 시나리오로 축소된다:
 *   - `description` 이 비어 있는데 `effects`(트레일링)가 채워진 행 → `description`
 *     이 encode 시 `null` 로 채워지고 decode 시에도 `null` 그대로 복원 → **gap 발생**.
 *   - `description` 만 채우고 `effects` 가 비어 있으면 `effects` 가 trailing 이라
 *     그대로 strip 되므로 gap 없음.
 * 정규화 없이 그대로 쓰면 `description?: string` 같은 타입 계약(`| null` 을
 * 포함하지 않음)이 런타임에 거짓이 되어, 소비처가 `.description` 을 문자열로
 * 취급하는 순간 "null 이니까 설명 없음"이 아니라 "타입에 없는 값이라 예측 불가"
 * 상태가 된다.
 *
 * **왜 generic-codec 을 고치지 않았나**: `decodeField`에서 record kind 전반에
 * `null → undefined` 정규화를 넣는 방법도 검토했으나, `stage.ts` 의
 * `starThresholds: Record<string, number> | null` 처럼 다른 스키마는 `null`
 * 을 **타입 계약에 포함된 유의미한 값**으로 쓰고 있어(스테이지 진짜 무자료
 * 상태) 전역 정규화는 그 계약을 흔든다(회귀 범위를 특정하기 어려움). 그래서
 * 이 정규화는 petBreakthrough 전용 헬퍼로 한정한다 — `decode(PetSchema, tuple)`
 * 를 호출하는 모든 소비처(Task 7 돌파 RPC 등)는 `.breakthroughs` 에 반드시
 * 이 함수를 적용해야 한다.
 */
export function normalizePetBreakthroughs<T extends Record<string, unknown>>(
  breakthroughs: T[] | undefined
): T[] | undefined {
  if (!breakthroughs) return breakthroughs;
  return breakthroughs.map((entry) => {
    const out = { ...entry };
    for (const key of PET_BREAKTHROUGH_NULLABLE_OPTIONAL_KEYS) {
      if (out[key as keyof T] === null) delete out[key as keyof T];
    }
    return out;
  });
}

export interface RawPetRow {
  id: number;
  max_level: number;
  grade: number;
  meta_tag_ids: number[] | null;
}

// `dev.pet_breakthrough` 행(pet_id, breakthrough_level 1~5) 1개 → petBreakthrough
// 서브스키마 객체. requiredStones 는 §4.2 전제상 항상 정확히 하나의 등급만 채워지므로
// 0 인 컬럼은 키 자체를 생략(레코드 kind — 필드 재구성이 아니라 값 없음). 나머지
// optional 필드(description/effects)도 DB 기본값(null/빈 배열)일 때는 키를 생략해
// petBreakthrough 스키마의 optional 계약과 맞춘다(petFromRaw 는 raw DB 값을 직접
// 조립하므로 pet.ts 상단의 normalizePetBreakthroughs — 디코드 후 포지셔널 gap 정규화
// — 와는 별개의, encode 이전 단계 정규화다). 레거시 4필드(effect_stats/effect_skills/
// synergy_bonus/seed_item_grade) 조립은 pet-bt-effects-migration-cleanup Task 3 에서
// 제거됐다 — `effects` 로 완전히 대체됐다.
function petBreakthroughFromRaw(b: RawRow): Record<string, unknown> {
  const requiredStones: Record<string, number> = {};
  if (b.required_stone_low) requiredStones.low = Number(b.required_stone_low);
  if (b.required_stone_mid) requiredStones.mid = Number(b.required_stone_mid);
  if (b.required_stone_high) requiredStones.high = Number(b.required_stone_high);

  const out: Record<string, unknown> = {
    breakthrough: b.breakthrough_level,
    requiredDupes: b.required_dupes,
    requiredStones,
  };
  if (b.description) out.description = b.description;
  // 신규 effects 컬럼(pet-bt-effect-decoupling Task 9 에서 dev.pet_breakthrough 에
  // 추가) — 현재는 컬럼이 없어 b.effects 는 항상 undefined 이므로 out.effects 는
  // 그대로 미설정으로 안전 폴백된다. jsonb 배열이므로 변형 없이 그대로 옮긴다.
  if (b.effects && (b.effects as BtEffect[]).length > 0) out.effects = b.effects as BtEffect[];
  return out;
}

export function petFromRaw(
  row: RawPetRow,
  raw: Pick<AllRawData, 'pet_grade' | 'pet_upgrade' | 'pet_breakthrough'>
) {
  const gradeRow = (raw.pet_grade ?? []).find((pg) => pg.grade === row.grade);
  const grade = gradeRow
    ? [{ grade: gradeRow.grade, returnSapphireAmount: gradeRow.return_sapphire_amount }]
    : [];

  const upgrades = (raw.pet_upgrade ?? [])
    .filter((u) => u.pet_id === row.id)
    .sort((a, b) => a.level - b.level)
    .map((u) => {
      const buffStats = u.character_buff_stats as Record<string, unknown> | null | undefined;
      const hpBonusRaw = buffStats?.HP;
      const hpBonus = hpBonusRaw == null ? 0 : Number(hpBonusRaw);
      return {
        level: u.level,
        requiredExp: u.required_exp,
        hpBonus: Number.isFinite(hpBonus) ? hpBonus : 0,
      };
    });

  // 파일럿 게이트: pet_breakthrough 행이 있는 펫만 breakthroughs 를 채운다(빈 배열이
  // 아니라 키 자체 생략 — PetSchema.breakthroughs 는 trailing 슬롯이라 undefined 여야
  // encode 시 trailing-strip 되고, decode 시 "돌파 대상 아님"이 undefined 로 왕복된다).
  const breakthroughs = (raw.pet_breakthrough ?? [])
    .filter((b) => b.pet_id === row.id)
    .sort((a, b) => a.breakthrough_level - b.breakthrough_level)
    .map(petBreakthroughFromRaw);

  return {
    id: row.id,
    maxLevel: row.max_level,
    grade,
    upgrades,
    metaTagIds: row.meta_tag_ids ?? [],
    ...(breakthroughs.length > 0 ? { breakthroughs } : {}),
  };
}

registerCategorySchema('serverPets', PetSchema);
